import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import { getBillingStatus, createPortalSession, createCheckout } from '../../api/client';

export default function Account() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const stored = localStorage.getItem('user');
  const user = stored ? JSON.parse(stored) : null;

  useEffect(() => {
    async function load() {
      try {
        const data = await getBillingStatus();
        setBilling(data);
      } catch (err) {
        console.error('Failed to load billing:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleManage() {
    setActionLoading(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to open portal:', err);
      setActionLoading(false);
    }
  }

  async function handleSubscribe() {
    setActionLoading(true);
    try {
      const { url } = await createCheckout();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to start checkout:', err);
      setActionLoading(false);
    }
  }

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <Navbar />
      <main className="flex-1 px-6 py-8">
        <div className="w-full max-w-lg mx-auto">
          <h1 className="text-xl font-heading font-semibold text-txt-primary mb-6">Account</h1>

          {/* Profile */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
            <h2 className="text-xs font-heading font-bold uppercase tracking-widest text-txt-secondary mb-3">Profile</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-txt-secondary">Name</span>
                <span className="font-semibold text-txt-primary">{user?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-txt-secondary">Email</span>
                <span className="font-semibold text-txt-primary">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-txt-secondary">Role</span>
                <span className="font-semibold text-txt-primary">{user?.role}</span>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h2 className="text-xs font-heading font-bold uppercase tracking-widest text-txt-secondary mb-3">Subscription</h2>

            {loading ? (
              <p className="text-sm text-txt-secondary">Loading...</p>
            ) : billing?.paymentExempt ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-txt-secondary">Plan</span>
                  <span className="font-semibold text-teal-dark">Full Access (Included)</span>
                </div>
                <p className="text-xs text-txt-secondary mt-2">
                  Your account includes full access as part of your organization.
                </p>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-txt-secondary">Status</span>
                  <span className={`font-semibold ${
                    billing?.access === 'full' ? 'text-teal-dark'
                      : billing?.access === 'trial' ? 'text-blue-600'
                      : 'text-red-600'
                  }`}>
                    {billing?.access === 'full' ? 'Active'
                      : billing?.access === 'trial' ? 'Free Trial'
                      : 'Expired'}
                  </span>
                </div>

                {billing?.subscriptionStatus === 'active' && billing?.subscriptionEndsAt && (
                  <div className="flex justify-between">
                    <span className="text-txt-secondary">Renews</span>
                    <span className="font-semibold text-txt-primary">{formatDate(billing.subscriptionEndsAt)}</span>
                  </div>
                )}

                {billing?.subscriptionStatus === 'canceled' && billing?.subscriptionEndsAt && (
                  <div className="flex justify-between">
                    <span className="text-txt-secondary">Access until</span>
                    <span className="font-semibold text-txt-primary">{formatDate(billing.subscriptionEndsAt)}</span>
                  </div>
                )}

                {billing?.access === 'trial' && billing?.trialExpiresAt && (
                  <div className="flex justify-between">
                    <span className="text-txt-secondary">Trial ends</span>
                    <span className="font-semibold text-txt-primary">{formatDate(billing.trialExpiresAt)}</span>
                  </div>
                )}

                <div className="pt-3">
                  {billing?.subscriptionStatus === 'active' || billing?.subscriptionStatus === 'canceled' ? (
                    <button
                      onClick={handleManage}
                      disabled={actionLoading}
                      className="text-sm text-gold-dark font-semibold hover:underline disabled:opacity-50"
                    >
                      {actionLoading ? 'Opening...' : 'Manage Subscription'}
                    </button>
                  ) : (
                    <button
                      onClick={handleSubscribe}
                      disabled={actionLoading}
                      className="btn-gold text-sm disabled:opacity-50"
                    >
                      {actionLoading ? 'Redirecting...' : 'Subscribe ($29/year)'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-sm text-txt-secondary hover:text-txt-primary"
          >
            &larr; Back
          </button>
        </div>
      </main>
    </div>
  );
}
